import React, {
  useCallback, useEffect, useMemo, useState,
} from "react";

import {
  CalendarOutlined,
  LeftOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Input, Table } from "antd";
import { toast } from "react-toastify";

import DatePicker from "../../../Common/DatePicker/DatePicker";
import Loading from "../../../Common/Loading";
import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import UsageMetricsTestActivityDetailModal from "./UsageMetricsTestActivityDetailModal";

import {
  formatTestsChange,
  mapLedgerActivitiesToRows,
  resolveLedgerSearchParams,
} from "../utils";

import { fetchUsageLedger } from "../../../../actions/usage";

import repeatFillIcon from "../../../../assets/images/subscriptions/repeat-fill.svg";

import "./UsageMetricsTestActivity.css";

const DEFAULT_PAGE_SIZE = 20;

export default function UsageMetricsTestActivity({ onBack, title = "Test Activity" }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [dateSelected, setDateSelected] = useState("");
  const [prevDateSelected, setPrevDateSelected] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = search.trim();
      setDebouncedSearch((prev) => {
        if (prev !== nextSearch) {
          setPage(1);
          setHasMore(true);
        }
        return nextSearch;
      });
    }, 350);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;
    const isFirstPage = page === 1;

    const loadLedger = async () => {
      if (isFirstPage) {
        setLoading(true);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      const filters = resolveLedgerSearchParams(debouncedSearch);
      const response = await fetchUsageLedger({
        ...filters,
        dateFrom,
        dateTo,
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        signal: controller.signal,
      });

      if (!isMounted || response.canceled) return;

      if (!response.success) {
        toast.error(response.message);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      const nextRows = mapLedgerActivitiesToRows(response.activities);
      setRecords((prev) => (isFirstPage ? nextRows : [...prev, ...nextRows]));
      setTotalCount(response.count);
      setHasMore(page * DEFAULT_PAGE_SIZE < response.count);
      setLoading(false);
      setLoadingMore(false);
    };

    loadLedger();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [debouncedSearch, dateFrom, dateTo, page]);

  const resetAndReload = useCallback(() => {
    setPage(1);
    setHasMore(true);
  }, []);

  const handleDateFilter = (startDate, endDate) => {
    setDateFrom(startDate);
    setDateTo(endDate || startDate);
    resetAndReload();
  };

  /**
   * On scroll stop, if scroll bar is at the bottom, load the next page of rows
   * @param {Object} e on scroll stop event values from StyledScrollbar
   */
  const handleScrollToBottom = (e) => {
    if (!e || loading || loadingMore || !hasMore) return;

    const tolerance = 3;
    const bottom = Math.abs(
      Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight),
    ) <= tolerance;

    if (bottom) {
      setPage((prev) => prev + 1);
    }
  };

  const columns = useMemo(() => [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Activity",
      dataIndex: "activity",
      key: "activity",
      render: (activity, record) => {
        if (activity == null || activity === "") return null;
        const badgeKey = record.activityKey;
        let adjustmentTone = "";
        if (badgeKey === "adjustment") {
          adjustmentTone = Number(record.tests) >= 0 ? "positive" : "negative";
        }
        return (
          <span className={`UsageMetricsTestActivity__Badge UsageMetricsTestActivity__Badge--${badgeKey}${adjustmentTone ? ` UsageMetricsTestActivity__Badge--${adjustmentTone}` : ""}`}>
            {badgeKey === "refund" && (
              <img
                src={repeatFillIcon}
                alt=""
                aria-hidden
                className="UsageMetricsTestActivity__BadgeIcon"
              />
            )}
            {activity}
          </span>
        );
      },
    },
    {
      title: "Test Details",
      dataIndex: "testDetails",
      key: "testDetails",
      className: "UsageMetricsTestActivity__Details",
      render: (testDetails) => {
        if (testDetails == null || testDetails === "") return null;
        return (
          <StyledTooltip title={testDetails} placement="top">
            <span className="UsageMetricsTestActivity__DetailsText">{testDetails}</span>
          </StyledTooltip>
        );
      },
    },
    {
      title: "Submission Name",
      dataIndex: "submissionName",
      key: "submissionName",
      className: "UsageMetricsTestActivity__Submission",
      ellipsis: true,
    },
    // {
    //   title: "Submission ID",
    //   dataIndex: "submissionId",
    //   key: "submissionId",
    //   className: "UsageMetricsTestActivity__Submission",
    // },
    {
      title: "Tests",
      dataIndex: "tests",
      key: "tests",
      render: (tests, record) => {
        if (tests == null || tests === "") return null;
        const amount = Number(tests);
        const isPositive = record.activityKey === "adjustment" ? amount >= 0 : amount > 0;
        return (
          <span className={isPositive ? "UsageMetricsTestActivity__Tests--positive" : "UsageMetricsTestActivity__Tests--negative"}>
            {formatTestsChange(tests)}
          </span>
        );
      },
    },
    {
      title: "Balance After",
      dataIndex: "balanceAfter",
      key: "balanceAfter",
    },
    {
      title: "Action By",
      dataIndex: "actionBy",
      key: "actionBy",
    },
  ], []);

  return (
    <section className="UsageMetricsTestActivity">
      <header className="UsageMetricsTestActivity__Header">
        <Button
          type="text"
          icon={<LeftOutlined />}
          className="UsageMetricsTestActivity__BackButton"
          onClick={onBack}
          aria-label="Back to usage metrics"
        />
        <div>
          <h1 className="UsageMetricsTestActivity__Title">{title}</h1>
          <p className="UsageMetricsTestActivity__Description">
            View purchases, and track test deductions or returns across categories.
          </p>
        </div>
      </header>

      <div className="UsageMetricsTestActivity__Controls">
        <div className="UsageMetricsTestActivity__ControlsLeft">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            prefix={<SearchOutlined />}
            placeholder="Search by submission name, submission ID, activity, or user"
            allowClear
            className="UsageMetricsTestActivity__Search"
          />
          <DatePicker
            handleSearchDate={handleDateFilter}
            value={dateSelected}
            onChange={setDateSelected}
            prevValue={prevDateSelected}
            setPrevValue={setPrevDateSelected}
            searchOnClear
            popoverPlacement="bottomLeft"
            datePickerDisplayProps={{
              displayClassName: `UsageMetricsTestActivity__DatePicker${dateSelected ? " UsageMetricsTestActivity__DatePicker--active" : ""}`,
              showText: true,
              iconTooltipTitle: "Filter by date",
              customCalendarIcon: (
                <CalendarOutlined className="UsageMetricsTestActivity__DatePickerIcon" />
              ),
            }}
          />
        </div>
        <span className="UsageMetricsTestActivity__Count">
          {`${totalCount} activity record${totalCount === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className="UsageMetricsTestActivity__TableWrap">
        <StyledScrollbar
          className="UsageMetricsTestActivity__ScrollBar"
          noScrollX
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
          onScrollStop={handleScrollToBottom}
        >
          <Table
            className="UsageMetricsTestActivity__Table"
            rowKey="id"
            columns={columns}
            dataSource={records}
            loading={loading}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setSelectedRecord(record);
                setDetailOpen(true);
              },
            })}
          />
          {loadingMore && (
            <div className="UsageMetricsTestActivity__LoadMore">
              <Loading />
            </div>
          )}
        </StyledScrollbar>
      </div>

      <UsageMetricsTestActivityDetailModal
        open={detailOpen}
        record={selectedRecord}
        columns={columns}
        onClose={() => setDetailOpen(false)}
        onAfterClose={() => setSelectedRecord(null)}
      />
    </section>
  );
}
